/* /admin/billing — current plan, upgrade modal, manage portal redirect.

   Reads `/api/billing/pricing` to know which plans are bookable in this
   environment (the BILLING_ENABLED flag + populated Price IDs). Hides the
   entire page if billing is off — production stays a clean "no such page"
   state without leaking that we have billing built. */
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Crown, ExternalLink, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PLAN_TAGLINE = {
  starter:  "For independent photographers just starting out",
  pro:      "For full-time photographers — most popular",
  ultimate: "For multi-shooter studios and agencies",
};

export default function AdminBilling() {
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Billing — Lumetier Admin";
    (async () => {
      try {
        const [pricingRes, subRes] = await Promise.all([
          api.get("/billing/pricing"),
          api.get("/billing/subscription").catch(() => ({ data: { plan: "beta", active: false } })),
        ]);
        setPricing(pricingRes.data);
        setSubscription(subRes.data);
      } catch (e) {
        toast.error("Couldn't load billing. " + (e?.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Toast on return from Stripe Checkout
  useEffect(() => {
    const status = searchParams.get("status");
    if (status === "success") {
      toast.success("Subscription started! Your new plan will be active in a moment.");
    } else if (status === "cancelled") {
      toast.info("Checkout cancelled — no changes to your plan.");
    }
  }, [searchParams]);

  const startCheckout = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan, interval, origin: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed: " + (e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Couldn't open billing portal: " + (e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const annualSavings = useMemo(() => {
    if (!pricing?.plans) return null;
    const pro = pricing.plans.find((p) => p.id === "pro");
    if (!pro) return null;
    return Math.round(((pro.monthly_price_usd * 12 - pro.annual_price_usd) / (pro.monthly_price_usd * 12)) * 100);
  }, [pricing]);

  if (loading) return <div className="p-8 text-stone-500">Loading billing…</div>;
  if (!pricing?.enabled) {
    return (
      <div className="p-8 max-w-2xl">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <h2 className="font-medium text-amber-900">Billing isn&rsquo;t available in this environment yet.</h2>
            <p className="text-sm text-amber-800/85 mt-1">You&rsquo;re currently on the Founder plan with no limits. We&rsquo;ll let you know when subscriptions go live.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = subscription?.plan || "beta";
  const isPaying = subscription?.active && currentPlan !== "beta" && currentPlan !== "founder";

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="admin-billing-page">
      <header className="mb-10">
        <h1 className="text-3xl font-light tracking-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Billing &amp; subscription
        </h1>
        <p className="text-sm text-stone-500 mt-2">Manage your Lumetier plan, payment method, and invoices.</p>
      </header>

      {/* Current plan card */}
      <section className="rounded-xl border border-stone-200 p-6 mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="current-plan-card">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {currentPlan === "founder" && <Crown className="w-4 h-4 text-amber-600" />}
            <span className="text-xs uppercase tracking-[0.32em] text-stone-500">Current plan</span>
          </div>
          <div className="text-2xl font-medium capitalize" data-testid="current-plan-label">{currentPlan}</div>
          {subscription?.status && (
            <div className="text-xs text-stone-500 mt-1">
              Status: <span className="font-medium">{subscription.status}</span>
              {subscription.current_period_end && (
                <> · Renews {new Date(subscription.current_period_end).toLocaleDateString()}</>
              )}
              {subscription.cancel_at_period_end && (
                <span className="text-amber-700"> · Cancels at period end</span>
              )}
            </div>
          )}
        </div>
        {isPaying && (
          <button
            onClick={openPortal}
            disabled={busy}
            data-testid="manage-billing-btn"
            className="inline-flex items-center gap-2 px-5 h-11 border border-stone-300 hover:border-stone-900 transition-colors text-sm rounded-md"
          >
            Manage billing <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </section>

      {/* Interval toggle */}
      <div className="flex items-center justify-center mb-8">
        <div className="inline-flex border border-stone-200 rounded-full p-1 bg-stone-50">
          {["monthly", "annual"].map((opt) => (
            <button
              key={opt}
              onClick={() => setInterval(opt)}
              data-testid={`interval-${opt}`}
              className={`px-5 py-2 text-sm rounded-full transition-all capitalize ${
                interval === opt ? "bg-white shadow-sm text-stone-900" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {opt}
              {opt === "annual" && annualSavings && (
                <span className="ml-2 text-xs text-emerald-700 font-medium">Save {annualSavings}%</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Plan grid */}
      <div className="grid md:grid-cols-3 gap-5">
        {pricing.plans.map((plan) => {
          const price = interval === "monthly" ? plan.monthly_price_usd : Math.round(plan.annual_price_usd / 12);
          const bookable = interval === "monthly" ? plan.bookable_monthly : plan.bookable_annual;
          const isCurrent = currentPlan === plan.id;
          const isRecommended = plan.id === "pro";
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`rounded-xl border p-6 flex flex-col ${
                isRecommended ? "border-stone-900 ring-1 ring-stone-900" : "border-stone-200"
              }`}
            >
              {isRecommended && (
                <div className="text-[10px] uppercase tracking-[0.28em] text-stone-900 font-medium mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Most popular
                </div>
              )}
              <h2 className="text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{plan.label}</h2>
              <p className="text-xs text-stone-500 mb-4 min-h-[2.5rem]">{PLAN_TAGLINE[plan.id]}</p>
              <div className="mb-1">
                <span className="text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>${price}</span>
                <span className="text-sm text-stone-500">/mo</span>
              </div>
              <div className="text-xs text-stone-400 mb-5">
                {interval === "annual" ? `Billed $${plan.annual_price_usd} yearly` : "Billed monthly"}
              </div>
              <ul className="space-y-2 mb-6 text-sm flex-1">
                <Feature ok>{plan.limits.storage_gb === "unlimited" ? "Unlimited storage" : `${plan.limits.storage_gb} GB storage`}</Feature>
                <Feature ok>{plan.limits.monthly_emails === "unlimited" ? "Unlimited emails" : `${plan.limits.monthly_emails.toLocaleString()} emails/mo`}</Feature>
                <Feature ok>{plan.limits.monthly_ai_msgs === "unlimited" ? "Unlimited AI Copilot" : `${plan.limits.monthly_ai_msgs} AI messages/mo`}</Feature>
                <Feature ok={plan.limits.white_label}>Remove Lumetier branding</Feature>
                <Feature ok={plan.limits.custom_sender_email}>Custom sender email</Feature>
                <Feature ok={plan.limits.raw_gallery_zip}>RAW gallery support</Feature>
                <Feature ok={plan.limits.advanced_typography}>Advanced typography</Feature>
                <Feature ok={plan.limits.custom_code}>Custom code / CSS</Feature>
                <Feature ok={plan.limits.api_access}>API access</Feature>
                <Feature ok={plan.limits.advanced_reporting}>Advanced reporting</Feature>
                <Feature ok>{plan.limits.team_seats} team {plan.limits.team_seats === 1 ? "seat" : "seats"}</Feature>
              </ul>
              {isCurrent ? (
                <div className="text-center py-3 text-sm text-stone-500 border border-stone-200 rounded-md">
                  Your current plan
                </div>
              ) : (
                <button
                  onClick={() => startCheckout(plan.id)}
                  disabled={!bookable || busy}
                  data-testid={`subscribe-${plan.id}`}
                  className={`px-4 h-11 inline-flex items-center justify-center text-sm rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isRecommended
                      ? "bg-stone-900 text-white hover:bg-stone-800"
                      : "border border-stone-300 hover:border-stone-900"
                  }`}
                >
                  {bookable ? "Start 14-day free trial" : "Coming soon"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-stone-400 text-center mt-8">
        14-day free trial · No charge until trial ends · Cancel anytime from the billing portal
      </p>
      <p className="text-xs text-stone-400 text-center mt-2">
        <Link to="/admin" className="underline underline-offset-4 hover:text-stone-600">Back to admin</Link>
      </p>
    </div>
  );
}

function Feature({ ok, children }) {
  return (
    <li className={`flex items-start gap-2 ${ok ? "" : "text-stone-300 line-through"}`}>
      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-emerald-600" : "text-stone-300"}`} />
      <span className={ok ? "text-stone-700" : ""}>{children}</span>
    </li>
  );
}
